/**
 * FollowupPills — the "you can also ask" suggestion row under an assistant
 * answer (design revamp).
 *
 * The prototype (`screens-chat.jsx`) renders an uppercase label
 * (`c.chat.followups`) above a wrapped row of small neutral pills; clicking a
 * pill re-sends that text as a new user message. Each pill is the design system
 * `Pill` with `onClick` (a focusable `<button>`); the label is the i18n
 * `followups` string.
 */
import { Pill } from "@garden/ui";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAssistantDesign } from "@/features/chat/useDesignFrame";

export interface FollowupPillsProps {
  items: string[];
  /** Re-send the chosen follow-up text as a new user message. */
  onPick: (text: string) => void;
}

export function FollowupPills({
  items,
  onPick,
}: FollowupPillsProps): ReactElement | null {
  const { t } = useTranslation("chat");
  const design = useAssistantDesign();
  if (items.length === 0) return null;

  if (design) {
    /*
     * The design's suggestion chips (spec §4.7): 1px `--gd-rule-3` on
     * `--gd-surface`, radius 999, `8px 16px`, hover moving border and ink to
     * `--gd-primary`. Plain buttons rather than the `@garden/ui` Pill, whose
     * own `--g-*` tokens would have to be overridden one by one.
     *
     * They stay UNDER the answer, where the product has always put them. The
     * design draws them above the composer, which is a different claim —
     * "these are the next things to ask" vs. "this answer suggests these" —
     * and moving them would decouple a suggestion from the turn that made it.
     * Flagged for the Task 12 audit rather than silently reconciled.
     *
     * The uppercase label is kept for the same reason: it is what tells a
     * screen reader (and a scanning eye) that a row of chips is suggestions
     * rather than an answer's own links.
     */
    return (
      <div className="mt-4" data-testid="chat-followups">
        {/* Semibold, the design's weight. `--gd-mono` is IBM Plex Mono in
            every language, and its 600 is a real face (design-world.css). */}
        <div
          className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.1em]"
          style={{ color: "var(--gd-ink-2)", fontFamily: "var(--gd-mono)" }}
        >
          {t("followups")}
        </div>
        <div className="flex flex-wrap gap-2">
          {items.map((item, i) => (
            <button
              key={`${i}-${item}`}
              type="button"
              onClick={() => onPick(item)}
              data-testid={`chat-followup-${i}`}
              /* The design's chip is 36px tall (`8px 16px` at 13px), under
                 the 44px floor. `.gd-hit-44` centres an invisible 44×44 box
                 over it — 4px of reach top and bottom, exactly the row's own
                 8px gap, so two stacked chips' targets touch but never
                 overlap and steal each other's taps. */
              className="gd-hit-44 max-w-full rounded-full border border-[var(--gd-rule-3)] bg-[var(--gd-surface)] px-4 py-2 text-left text-[14px] leading-snug text-[var(--gd-ink-2)] transition-colors duration-150 hover:border-[var(--gd-primary)] hover:text-[var(--gd-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)] motion-reduce:transition-none"
              style={{ fontFamily: "var(--gd-text)" }}
            >
              <span className="line-clamp-2">{item}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="chat-followups">
      <div className="mb-2 text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--g-ink-2)]">
        {t("followups")}
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item, i) => (
          <Pill
            key={`${i}-${item}`}
            tone="neutral"
            size="sm"
            wrap
            onClick={() => onPick(item)}
            className="max-w-full"
          >
            {/*
             * Two lines, not one. `line-clamp-1` truncated a real follow-up
             * ("Сколько клея нужно на 12 м² при плитке 30×30?") to about a
             * third of itself, which turns a suggestion into a guess — the
             * user cannot choose what they cannot read. Two lines fits every
             * follow-up the backend emits, in RU and in the ~20%-longer KY.
             */}
            <span
              data-testid={`chat-followup-${i}`}
              className="line-clamp-2 text-left leading-snug"
            >
              {item}
            </span>
          </Pill>
        ))}
      </div>
    </div>
  );
}

export default FollowupPills;
